import { normalizeCustomFieldOption } from "./workkardFields";
import { fileIconBasedOnFileName } from "../../containers/WorkkardDetail/utils";

const asString = (value, defaultValue = "") =>
  typeof value === "string" ? value : defaultValue;

const asNullableString = (value) =>
  typeof value === "string" ? value : null;

const asNumber = (value, defaultValue = 0) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue)
      ? parsedValue
      : defaultValue;
  }

  return defaultValue;
};

const asBoolean = (value) => value === true;

const asArray = (value) =>
  Array.isArray(value) ? value : [];

const asNullableValue = (value) =>
  value === undefined ? null : value;

function resolveFileType(rawType, fileName) {
  if (rawType === "Image" || rawType === "Video") return rawType;
  const ext = String(fileName || "").split(".").pop()?.toLowerCase();
  return fileIconBasedOnFileName(ext) === "Image" ? "Image" : rawType;
}

// A note attachment as returned by /1.0/getclmnotes (notes.v1.ActivityNoteFile).
// The legacy keys are kept as fallbacks so a response from the old notes API
// still normalises.
export function normalizeNoteFile(file = {}) {
  const guid = asString(file.guid) || asString(file.file_guid);
  const url = asString(file.file_url);
  const thumb = asString(file.thumb_file_url) || asString(file.thumbnail_image) || url;

  return {
    FileId: asNumber(file.id),
    FileName: asString(file.file_name),
    FileGuid: guid,
    FileType: resolveFileType(asString(file.file_type), file.file_name),
    FileSize: asString(file.file_size),
    FileUrl: url,
    // The projection serves one download URL per guid; preview and open share it.
    FilePreviewUrl: asString(file.file_preview_url) || url,
    ThumbFileUrl: thumb,
    OpenFileUrl: asString(file.open_file_url) || url,
    Caption: asString(file.caption),
    CanModifyCaption:
      file.can_modify_caption === undefined ? true : asBoolean(file.can_modify_caption),
    VideoFileName: asString(file.video_file_name) || asString(file.processed_file_name),
    VideoThumbFileName: asString(file.video_thumb_file_name) || asString(file.thumbnail_image),
  };
}

// A note template field as returned by /1.0/getclmnotes (customfield.v1.CustomField).
// Both `Label` and `LabelName` are emitted: the note card reads Label, while the
// field-rendering engine's grouping helper falls back to LabelName.
export function normalizeNoteCustomField(field = {}) {
  const options = Array.isArray(field.items)
          ? field.items.map(normalizeCustomFieldOption)
          : null;
  const label = asString(field.label);
  return {
    Id: asNumber(field.custom_field_id),
    CustomFieldId: asNumber(field.custom_field_id),
    FieldKey: asString(field.field_key),
    FieldGroup: asString(field.field_group_name),
    Label: label,
    LabelName: label,
    Name: asString(field.field_name),
    FieldType: asString(field.field_type),
    Group: asString(field.group),
    Value: asNullableValue(field.value),
    Options: options,
    IsRequired: asBoolean(field.is_required),
    IsNonEditable: asBoolean(field.is_non_editable),
    IsSystemField: asBoolean(field.is_system_field),
    MaxLength: asNumber(field.max_length),
    FieldSettingConfig: asNullableString(field.setting_configuration),
    FieldSettings: asNullableString(field.settings),
    Sequence: asNumber(field.sequence),
    SequenceNumber: asNumber(field.sequence) || asNumber(field.sequence_number),
    MapFieldKey: asString(field.map_field_key),
    Description: asNullableString(field.description),
    PreInitScript: asNullableString(field.pre_init_script),
    PreSaveScript: asNullableString(field.presave_script ?? field.pre_save_script),
  };
}

export function normalizeNote(note = {}) {
  const module = asString(note.module);
  const isWorkkardNote = module.toLowerCase() === 'workkard';
  // The projection returns one `note_files` array for both modules; the note card
  // and the image gallery read a different key per module.
  const rawFiles = asArray(note.note_files).length
    ? asArray(note.note_files)
    : asArray(note.activity_note_files);
  const files = rawFiles.map(normalizeNoteFile);

  return {
    Id: asNumber(note.id),
    Module: module,
    ActivityId: asNumber(note.activity_id),
    OwnerOrgId: asNumber(note.owner_org_id),
    AssigneeOrgId: asNumber(note.assignee_org_id),
    CreatedBy: asNumber(note.created_by),
    CreatedDate: asString(note.created_date),
    ActivityTitle: asNullableString(note.activity_title),
    WorkkardId: asNumber(note.workkard_id),
    NoteTemplateId: asNumber(note.note_template_id),
    IsNoteTemplateDefault: asBoolean(note.is_note_template_default),
    IsSystem: asBoolean(note.is_system),
    IsTemplateAssociated: asBoolean(note.is_template_associated),
    NoteTemplateName: asString(note.note_template_name),
    IsNoteTemplateActive: asBoolean(note.is_note_template_active),
    WorkkardNo: asString(note.workkard_no),
    // Aliases the global-search variant of the note card links against.
    WorkkardNumber: asString(note.workkard_number) || asString(note.workkard_no),
    workkardId: asNumber(note.workkard_id),
    activityId: asNumber(note.activity_id),
    Description: asString(note.description),
    SearchCreatedDate: asNumber(note.search_created_date),
    UpdatedBy: asNumber(note.updated_by),
    UpdatedDate: asString(note.updated_date),
    SearchUpdatedDate: asNumber(note.search_updated_date),
    Type: asString(note.type),
    IsActive: asBoolean(note.is_active),
    IsDeleted: asBoolean(note.is_deleted),
    Resolution: asNumber(note.resolution),
    FileName: asString(note.file_name),
    FileType: asString(note.file_type),
    FileSize: asString(note.file_size),
    Name: asString(note.name),
    UserName: asString(note.user_name),
    ProfileImage: asString(note.profile_image),
    FileNoteId: asNullableValue(note.file_note_id),
    OrganizationId: asNumber(note.organization_id),
    FromResolution: asString(note.from_resolution),
    ToResolution: asString(note.to_resolution),
    FileGuid: asString(note.file_guid),
    PublicIdentifier: asNumber(note.public_identifier),
    PrivateIdentifier: asNumber(note.private_identifier),
    PersonalIdentifier: asNumber(note.personal_identifier),
    Caption: asNullableString(note.caption),
    VideoFileName: asNullableString(note.video_file_name),
    VideoThumbFileName: asNullableString(note.video_thumb_file_name),
    PrettyCreatedDate: asString(note.pretty_created_date),
    ParentId: asNullableValue(note.parent_id),
    ParentIds: asNullableValue(note.parent_ids),
    // Per-note action flags computed by web-view, mirroring the diary panel.
    CanDeleteNote: asBoolean(note.can_delete ?? note.can_delete_note),
    CanEditNote: asBoolean(note.can_edit ?? note.can_edit_note),
    CanReviewNote: asBoolean(note.can_review),
    CanPublishNote: asBoolean(note.can_publish),
    IsSysUser: asBoolean(note.is_sys_user),
    IsUserDeleted: asBoolean(note.is_user_deleted),
    IsUserActive: asBoolean(note.is_user_active),
    IsOrganizationDeleted: asBoolean(note.is_organization_deleted),
    CanCloneNote: asBoolean(note.can_clone ?? note.can_clone_note),
    ActivityNoteFiles: isWorkkardNote ? [] : files,
    WorkkardFiles: isWorkkardNote ? files : [],
    ActivityNoteCustomFieldViews: asArray(note.custom_fields).map(normalizeNoteCustomField),
    OrganizationName: asString(note.organization_name),
    NoteCategory:
      asString(note.note_category) ||
      (isWorkkardNote ? 'WorkkardNoteFile' : 'ActivityNote'),
    FirstName: asString(note.first_name),
    LastName: asString(note.last_name),
    FromAssignee: asNullableValue(note.from_assignee),
    ToAssignee: asNullableValue(note.to_assignee),
    FromAssigneeOrg: asNullableValue(note.from_assignee_org),
    ToAssigneeOrg: asNullableValue(note.to_assignee_org),
    IsReviewed: asBoolean(note.is_reviewed),
    ReviewedBy: asString(note.reviewed_by),
    ReviewerId: asNumber(note.reviewer_id) || asNumber(note.reviewed_by),
    IsReviewerActive: asBoolean(note.is_reviewer_active),
    NoteReason: asString(note.note_reason),
    ReviewedDate: asNullableString(note.reviewed_date_string),
    UpdatedDateEpoch: asNumber(note.updated_date_epoch),
    ReviewedDateEpoch: asNumber(note.reviewed_date_epoch),
    DescriptionJson: asNullableValue(note.description_json),
    ReviewedDateString: asString(note.reviewed_date_string),
    PublishedDateString: asString(note.published_date_string),
    iswkshared: asBoolean(note.iswkshared),
    InternalToPublic: asBoolean(note.internal_to_public),
    PublishedBy: asString(note.published_by),
    PublisherId: asNumber(note.published_by) || asNumber(note.updated_by),
    IsPublisherActive: asBoolean(note.is_publisher_active),
    ActivityNoteViewOrgIds: asNullableValue(note.activity_note_view_org_ids),
    NoteAttachmentType: asNullableString(note.note_attachment_type),
    NoteCreatedDate: asString(note.note_created_date),
    NoteType: asNullableString(note.note_type),
    GUID: asNullableString(note.guid),
  };
}